"use client";

import { useEffect, useRef, useState, useCallback, useLayoutEffect } from "react";

interface ImageCanvasProps {
    src: string;
    width?: number | "auto"; // 支持 "auto" 自适应宽度
    height?: number | "auto"; // 支持 "auto" 自适应高度
    objectFit?: "cover" | "contain" | "fill";   // 支持 object-fit 属性
    isPositionStart?: boolean;
}

const ImageCanvas: React.FC<ImageCanvasProps> = ({ src, width = "auto", height = "auto", objectFit = 'fill', isPositionStart = false }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const parentRef = useRef<HTMLDivElement>(null);
    const [canvasWidth, setCanvasWidth] = useState<number>(0);
    const [canvasHeight, setCanvasHeight] = useState<number>(0);
    const [lastRenderedWidth, setLastRenderedWidth] = useState<number>(0);
    const [lastRenderedHeight, setLastRenderedHeight] = useState<number>(0);

    // 监听父元素大小变化，更新 canvas 宽度、高度（带防抖）
    const updateCanvasSize = useCallback(() => {
        console.log("更新 canvas 尺寸", parentRef.current);
        if (parentRef.current) {
            if (width === "auto") {
                const newWidth = parentRef.current.clientWidth;
                if (newWidth !== canvasWidth) {
                    console.log("更新 canvasWidth:", newWidth);
                    setCanvasWidth(newWidth);
                }
            }
            if (height === "auto") {
                const newHeight = parentRef.current.clientHeight;
                if (newHeight !== canvasHeight) {
                    console.log("更新 canvasHeight:", newHeight);
                    setCanvasHeight(newHeight);
                }
            }
        } 
    }, [width, height, canvasWidth, canvasHeight]);

    // 监听 resize 事件，防止 canvas 不断变化
    useLayoutEffect(() => {
        if (typeof window === "undefined") return;
        updateCanvasSize(); // 组件挂载时计算尺寸

        const resizeObserver = new ResizeObserver(() => {
            setTimeout(updateCanvasSize, 100); // **增加 debounce 防止频繁触发**
        });

        if (parentRef.current) resizeObserver.observe(parentRef.current);

        return () => resizeObserver.disconnect();
    }, [updateCanvasSize]);

    // **优化绘制，使用 Offscreen Canvas**
    useEffect(() => {
        if (typeof window === "undefined") return;
        const canvas = canvasRef.current;
        if (!canvas) return;

        const finalWidth = width === "auto" ? canvasWidth : width;
        if (finalWidth === lastRenderedWidth) return; // **避免重复渲染相同宽度**

        const finalHeight = height === "auto" ? canvasHeight : height;
        if (finalHeight === lastRenderedHeight) return; // **避免重复渲染相同高度**

        const dpr = window.devicePixelRatio || 1;
        canvas.width = finalWidth * dpr;
        canvas.height = finalHeight * dpr;
        canvas.style.width = `${finalWidth}px`;
        canvas.style.height = `${finalHeight}px`;

        console.log("Canvas 设置宽高:", canvas.width, canvas.height);

        // **使用离屏 canvas 避免闪烁**
        const offscreenCanvas = document.createElement("canvas");
        offscreenCanvas.width = canvas.width;
        offscreenCanvas.height = canvas.height;

        const ctx = offscreenCanvas.getContext("2d");
        if (!ctx) return;

        ctx.scale(dpr, dpr);

        const img = new Image();
        img.crossOrigin = "anonymous";
        img.src = src;

        img.onload = () => {
            ctx.clearRect(0, 0, finalWidth, finalHeight);
        
            let drawWidth = finalWidth;
            let drawHeight = finalHeight;
            let offsetX = 0;
            let offsetY = 0;
        
            const imgAspect = img.width / img.height;
            const canvasAspect = finalWidth / finalHeight;
        
            if (objectFit === "contain") {
                if (imgAspect > canvasAspect) {
                    // 图片更宽，按宽度缩放
                    drawWidth = finalWidth;
                    drawHeight = finalWidth / imgAspect;
                    if (!isPositionStart) {
                        offsetY = (finalHeight - drawHeight) / 2;
                    }
                } else {
                    // 图片更高，按高度缩放
                    drawHeight = finalHeight;
                    drawWidth = finalHeight * imgAspect;
                    if (!isPositionStart) { 
                        offsetX = (finalWidth - drawWidth) / 2;
                    }
                }
            } else if (objectFit === "cover") {
                if (imgAspect > canvasAspect) {
                    // 图片更宽，按高度放大并裁剪
                    drawHeight = finalHeight;
                    drawWidth = finalHeight * imgAspect;
                    if (!isPositionStart) {
                        offsetY = (finalHeight - drawHeight) / 2;
                    }
                } else {
                    // 图片更高，按宽度放大并裁剪
                    drawWidth = finalWidth;
                    drawHeight = finalWidth / imgAspect;
                    if (!isPositionStart) {
                        offsetY = (finalHeight - drawHeight) / 2;
                    }
                }
            }
        
            ctx.drawImage(img, offsetX, offsetY, drawWidth, drawHeight);
            
            const mainCtx = canvas.getContext("2d");
            if (mainCtx) {
                mainCtx.clearRect(0, 0, finalWidth, finalHeight);
                mainCtx.drawImage(offscreenCanvas, 0, 0);
            }
        
            setLastRenderedWidth(finalWidth);
            setLastRenderedHeight(finalHeight);
        };

        img.onerror = (err) => console.error("图片加载失败:", err);
    }, [src, canvasWidth, height, width, lastRenderedWidth, canvasHeight, lastRenderedHeight, objectFit]);

    return (
        <div ref={parentRef} style={{ width: width === "auto" ? "100%" : width, height: height === "auto" ? "100%" : height }}>
            <canvas ref={canvasRef} style={{ background: "transparent" }} />
        </div>
    );
};

export default ImageCanvas;